import React, { useEffect, useState } from "react";
import { getBranch } from "../api/client";
import { useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { getImageUrl } from "../utils/imageUtils";

// Fix for default marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const BranchDetail = ({ settings, lang }) => {
    const { slug } = useParams();
    const [branchData, setBranchData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getBranch(slug);
                setBranchData(response.data);
            } catch (error) {
                console.error("Failed to fetch branch details:", error);
            } finally {
                setLoading(false);
            }
        };
        if (slug) fetchData();
    }, [slug]);

    const getText = (en, ar) => (lang === "en" ? en : ar);
    const t = (key) => {
        if (!settings) return "";
        const globalData = lang === "en" ? settings.en : settings.ar;
        return (globalData && globalData[key]) ? globalData[key] : "";
    };


    // if (loading) return <div className="text-center py-5">Loading...</div>; // Removed blocking loader
    if (!loading && !branchData) return <div className="text-center py-5">Branch not found.</div>;

    // Initialize variables to avoid crash on first render while loading
    let branchName = "";
    let address = "";
    let city = "";
    let branch = null;
    let images = [];

    if (branchData) {
        branch = branchData.branch;
        images = branchData.images;
        branchName = lang === 'en' ? branch.branch_name_en : branch.branch_name_ar;
        address = lang === 'en' ? branch.address_en : branch.address_ar;
        city = lang === 'en' ? branch.city_en : branch.city_ar;
    }

    return (
        <>
            {loading ? (
                <div className="container py-5 text-center" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div>
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-3 text-muted">{getText("Loading details...", "جاري تحميل التفاصيل...")}</p>
                    </div>
                </div>
            ) : (
                <>
                    <header className="py-5 bg-body header1">
                        <div className="container py-5">
                            <div className="row align-items-center">
                                <div className="col-lg-8" data-aos="fade-right">
                                    <nav aria-label="breadcrumb" className="mb-3">
                                        <ol className="breadcrumb">
                                            <li className="breadcrumb-item">
                                                <Link to="/map" className="text-decoration-none text-muted">
                                                    {getText("Global Presence", "تواجدنا العالمي")}
                                                </Link>
                                            </li>
                                            <li className="breadcrumb-item active text-primary px-2" aria-current="page">
                                                {branchName}
                                            </li>
                                        </ol>
                                    </nav>
                                    <h1 className="display-4 fw-bold mb-2">{branchName}</h1>
                                    <p className="lead text-muted mb-0">{city}</p>
                                </div>
                            </div>
                        </div>
                    </header>

                    <section className="py-5 bg-card">
                        <div className="container">
                            <div className="row g-5">
                                <div className="col-lg-4" data-aos="fade-up">
                                    <div className="card-custom p-4 mb-4">
                                        <h4 className="fw-bold mb-4">{getText("Contact Information", "معلومات الاتصال")}</h4>

                                        <div className="d-flex gap-3 mb-4">
                                            <div className="text-primary"><i className="ri-map-pin-line fs-4"></i></div>
                                            <div>
                                                <h6 className="fw-bold mb-1">{getText("Address", "العنوان")}</h6>
                                                <p className="text-muted small mb-0">{address}</p>
                                            </div>
                                        </div>

                                        <div className="d-flex gap-3 mb-4">
                                            <div className="text-primary"><i className="ri-phone-line fs-4"></i></div>
                                            <div>
                                                <h6 className="fw-bold mb-1">{getText("Phone", "الهاتف")}</h6>
                                                <p className="text-muted small mb-0">
                                                    <a href={`tel:${branch.phone}`} className="text-muted text-decoration-none">{branch.phone}</a>
                                                </p>
                                            </div>
                                        </div>

                                        <div className="d-flex gap-3 mb-4">
                                            <div className="text-primary"><i className="ri-mail-line fs-4"></i></div>
                                            <div>
                                                <h6 className="fw-bold mb-1">{getText("Email", "البريد الإلكتروني")}</h6>
                                                <p className="text-muted small mb-0">
                                                    <a href={`mailto:${branch.email}`} className="text-muted text-decoration-none">{branch.email}</a>
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-custom p-4">
                                        <h4 className="fw-bold mb-4">{getText("Working Hours", "ساعات العمل")}</h4>
                                        <div className="d-flex gap-3">
                                            <div className="text-primary"><i className="ri-time-line fs-4"></i></div>
                                            <div>
                                                <p className="text-muted mb-0">{branch.start || getText("24 Hours", "24 ساعة")}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-8" data-aos="fade-up" data-aos-delay="100">
                                    <div className="mb-5">
                                        <h4 className="fw-bold mb-4">{getText("Location", "الموقع")}</h4>
                                        <div className="card-custom p-2" style={{ height: '400px' }}>
                                            <MapContainer center={[branch.lat, branch.lng]} zoom={13} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                                                <TileLayer
                                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                />
                                                <Marker position={[branch.lat, branch.lng]}>
                                                    <Popup>
                                                        <b>{branchName}</b><br />{address}
                                                    </Popup>
                                                </Marker>
                                            </MapContainer>
                                        </div>
                                    </div>

                                    {images && images.length > 0 && (
                                        <div>
                                            <h4 className="fw-bold mb-4">{getText("Office Gallery", "معرض الصور")}</h4>
                                            <div className="row g-3">
                                                {images.map((img, i) => (
                                                    <div className="col-md-6" key={i}>
                                                        <img src={getImageUrl(img.image, 'imgPro')} className="gallery-img shadow-sm w-100 object-fit-cover" alt="Office Photo" />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                </>
            )}
        </>
    );
};

export default BranchDetail;
